import { getText, interpolate } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type SizeEstimateTreePropertyDescriptor,
  type SpeciesTreePropertyDescriptor,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface IsNativeSpeciesFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function IsNativeSpeciesField<T extends SpeciesTreePropertyDescriptor>({
  onChange,
  treeDescriptor,
}: IsNativeSpeciesFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.species,
    },
  );

  const trunkType = treeDescriptor.properties.get(
    TreePropertyType.trunk_type,
  ) as TrunkTypeTreePropertyDescriptor | undefined;

  const sizeEstimate = treeDescriptor.properties.get(TreePropertyType.size) as
    | SizeEstimateTreePropertyDescriptor
    | undefined;

  const species = treeDescriptor.properties.get(TreePropertyType.species) as
    | T
    | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.species,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        {interpolate("Is the {{what}} a native species?", {
          what:
            sizeEstimate?.value === "vegetation" ||
            trunkType?.value === "vegetation"
              ? getText("vegetation")
              : getText("tree"),
        })}
      </FieldLabel>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("native");
          }}
          pressed={species?.value === "native"}
          variant="rounded"
        >
          {getText("Yes")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("non-native");
          }}
          pressed={species?.value === "non-native"}
          variant="rounded"
        >
          {getText("No")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("unsure");
          }}
          pressed={species?.value === "unsure"}
          variant="rounded"
        >
          {getText("Unsure")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
